/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * Se termino utilizando una libreria para pluralizar palabras por todas las excepciones existentes en español
 * igualmente se agregaron tests para verificar su correcto funcionamiento y fiabilidad
 */
import { isAxiosError } from 'axios';
import classNames, { type ArgumentArray } from 'classnames';
import Cookies from 'js-cookie';
import { NavigateFunction } from 'react-router-dom';
import { toast } from 'react-toastify';
import Plural from 'rosaenlg-pluralize-es';
import { twMerge } from 'tailwind-merge';
import config from 'tailwindcss/defaultConfig';

import { ONLY_LETTERS_AND_NUMBERS } from './regex';
import { URLs } from './urls';

import ROUTES from '@/router/routes';
import {
  AUTH0_NETWORKS,
  KeysOf,
  ARG_OR_EXT,
  Maybe,
  DocumentTypesValues,
  TypeIdNombre,
  SelectValue,
  Prettify,
  GDTApiErrors,
  Optional,
  GDTApiTimeout,
} from '@/types';
import { InvitationType } from '@/types/invites';
import {
  TacticOnField,
  PlayerDataComponentInField,
  PlayerComponentInField,
  PlayerClub,
  TemplatePlayer,
  PlayerF5,
  PlayersOnFieldF5,
  EnumTeamContext,
} from '@/types/team';
import { PlayerHistoricF5, PlayerMyTeamF5 } from '@/types/teamf5';
import {
  PLAYER_FIELD_ABBREVIATION_BY_POSITION,
  TEXTS,
  TACTICS_ARR,
  PLAYER_POSITION_BY_ID,
  POSITIONS_LABELS,
  GENERICS_WORDS,
} from '@/utils/constants';

const screens = config.theme?.screens;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const mediaSize = `(min-width: ${screens.lg ?? '1024px'})`;

export const compareArray = (
  a: string[] | number[],
  b: string[] | number[]
) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

export const areArrayEqual = (
  a: (string | number)[],
  b: (string | number)[]
): boolean => {
  if (a.length !== b.length) return false;

  const map = new Map<string | number, number>();

  for (const val of a) {
    map.set(val, (map.get(val) ?? 0) + 1);
  }

  for (const val of b) {
    if (!map.has(val)) return false;

    const count = map.get(val)! - 1;

    if (count < 0) return false;

    map.set(val, count);
  }

  return true;
};

// filtro para
export const findDifferentsStringsArrays = (
  array1: string[],
  array2: string[]
): string[] => {
  // Crear conjuntos (Sets) a partir de los arrays para una búsqueda más eficiente
  const set1 = new Set(array1);
  const set2 = new Set(array2);

  // Función auxiliar para encontrar elementos únicos
  const findUnique = (setA: Set<string>, setB: Set<string>) => {
    return [...setA].filter((item) => !setB.has(item));
  };

  // Encontrar elementos únicos en ambos conjuntos
  const uniqueBetweenArrays = findUnique(set2, set1);

  return uniqueBetweenArrays;
};

// concateno sugerencias de busquedas de 3 en 3
export const concatenateArraysWithIntervals = (
  arr1: any[],
  arr2: any[],
  interval: number
) => {
  if (Array.isArray(arr1) && Array.isArray(arr2)) {
    const result = [];
    const maxLength = Math.max(arr1.length, arr2.length);

    for (let i = 0; i < maxLength; i += interval) {
      result.push(
        ...arr1.slice(i, i + interval),
        ...arr2.slice(i, i + interval)
      );
    }

    return result;
  } else {
    throw new Error('Los parametros de la funcion deben ser arrays');
  }
};

export const haveSameElements = (
  arr1: string[] | number[] | undefined,
  arr2: string[] | number[] | undefined
) => {
  if (!arr1 || !arr2) {
    return false;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = [...arr1].sort();
  const sortedArr2 = [...arr2].sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }

  return true;
};

export const scrollSmooth = (position: ScrollLogicalPosition = 'start') => {
  document.body.scrollIntoView({
    block: position,
    behavior: 'smooth',
  });
};

export const lockScroll = (lock = false) => {
  if (lock) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'auto';
  }
};

/**
 * Formats a number with the specified locale.
 * @param numbers - The number to format.
 * @returns The formatted number as a string.
 */
export const formatNumberToLocale = (
  numbers: number | string,
  locale = 'es-AR'
): string => {
  const num = Number(numbers);

  if (!isNaN(num) && typeof num === 'number') {
    return new Intl.NumberFormat(locale).format(num);
  }

  throw new Error('Not a number');
};

export const selectSocialNetwork = (
  networkType?: KeysOf<typeof AUTH0_NETWORKS>
) => {
  return networkType ? AUTH0_NETWORKS[networkType] : '';
};

export const cn = (...inputs: ArgumentArray) => {
  return twMerge(classNames(inputs));
};

export function on<T extends Window | Document | HTMLElement | EventTarget>(
  obj: T | null,
  ...args:
    | Parameters<T['addEventListener']>
    | [string, () => void | null, ...any]
): void {
  if (obj && obj.addEventListener) {
    obj.addEventListener(
      ...(args as Parameters<HTMLElement['addEventListener']>)
    );
  }
}

export function off<T extends Window | Document | HTMLElement | EventTarget>(
  obj: T | null,
  ...args:
    | Parameters<T['removeEventListener']>
    | [string, () => void | null, ...any]
): void {
  if (obj && obj.removeEventListener) {
    obj.removeEventListener(
      ...(args as Parameters<HTMLElement['removeEventListener']>)
    );
  }
}

// Valida que el valor sea es extranjero o argentino
export const isArgOrExt = (value: Maybe<DocumentTypesValues>) => {
  return value === ARG_OR_EXT.arg;
};

// Valida si un campo fue tocado y si tiene errores
export const validateFieldError = (
  isTouched?: boolean,
  hasErrorsMsg?: string,
  valueLength?: Maybe<number>
) => {
  if ((isTouched && hasErrorsMsg) || (valueLength && hasErrorsMsg)) {
    return hasErrorsMsg;
  }

  return undefined;
};

export const parseToSelectOption = (values: TypeIdNombre[]): SelectValue[] => {
  return values.map((value) => ({
    value: value.id,
    label: value.nombre,
  }));
};

export const parseObjectToSelectOption = (
  value: Partial<TypeIdNombre>
): SelectValue => {
  return {
    value: value.id ?? 0,
    label: value.nombre ?? '',
  };
};

// Las 2 siguientes funciones hacen casi el mismo trabajo a continuacion se dejara ejemplo de que pueden recibir y que pueden retornar

// Puede recibir 1000000 y retornar 1M => formatToBalanceArs(1500.50) => 1.500,50 (Formato Balance en ARS)
export const formatToBalanceArs = (number: number) => {
  if (number >= 1_000_000) {
    return formatMillionBalance(number);
  }

  return formatNumberToLocale(number);
};

export const formatMillionBalanceFromString = (numberString: string) => {
  const numberRegex = /^\d+$/;
  // Validate if is only numbers
  if (numberRegex.test(numberString)) {
    return formatMillionBalance(parseInt(numberString));
  } else {
    return NaN;
  }
};

export const formatMillionBalance = (number: number) => {
  const million = 1_000_000;

  if (!Number.isFinite(number)) {
    return NaN;
  }

  if (number < 10_000) {
    return `${number}`;
  } else {
    return `${number / million}M`;
  }
};

// Toma una palabra en singular en español y devuelve su forma plural:
export const pluralizeWord = (word?: Maybe<string>) => {
  if (word) {
    return Plural(word);
  }

  return null;
};

export const checkTeamHasCaptain = (team?: PlayerClub[]) => {
  const hasCaptain = team?.some((player) => {
    if (player && 'props' in player) {
      const playerData = player?.props as PlayerDataComponentInField;
      return playerData?.data?.capitan;
    }

    return player?.capitan;
  });

  return Boolean(hasCaptain);
};

export const checkIfPositionsAreCompleted = (team?: PlayerClub[]) => {
  const teamIsComplete = team?.every((player) => {
    return player?.id !== undefined;
  });

  return Boolean(teamIsComplete);
};

type TeamType = Prettify<
  Omit<TacticOnField, 'substitutes' | 'goalkeepers'> & {
    goalkeepers: JSX.Element[];
  }
>;

type TeamTypeF5 = Prettify<PlayersOnFieldF5>;

type Vacant = keyof TeamType;

/**
 * Esta funcion se encarga de encontrar la proxima posicion vacante, y te devuelve un objeto
 * con la posicion, el indice y el orden de la misma
 */
export const getAvailablePositionOnField = (team: TeamType) => {
  const vacantOrder = ['goalkeepers', 'defenders', 'midfielders', 'forwards'];

  const isPositionEmptyByCategory = (team: TeamType, category: Vacant) => {
    let index = 0;

    for (const position of team[category]) {
      const positionData = position?.props as PlayerDataComponentInField;
      if (positionData?.data?.id === undefined) {
        return {
          index,
          orden: positionData?.data?.orden,
        };
      }

      index++;
    }

    return null;
  };

  for (const category of vacantOrder) {
    const categoryType = category as unknown as Vacant;
    const vacantOrden = isPositionEmptyByCategory(team, categoryType);

    if (vacantOrden) {
      return {
        position: PLAYER_FIELD_ABBREVIATION_BY_POSITION[categoryType],
        index: vacantOrden.index,
        orden: vacantOrden.orden,
      }; // Exit the loop as soon as a valid category is found
    }
  }
};

export const getAvailablePositionOnFieldF5 = (team: TeamTypeF5) => {
  if (!team.goalkeeper?.props.data.id) {
    // Si no tengo seleccionado un arquero en mi equipo.
    return {
      position: PLAYER_POSITION_BY_ID[1],
      orden: 0,
    };
  }

  // Buscar el primer jugador de campo no asignado.
  const order = team.fieldPlayers.findIndex(
    (element) => !element.props.data.id
  );

  // Si todos los jugadores de campo estan asignados.
  if (order === -1) return;

  return {
    position: POSITIONS_LABELS.all_field_players,
    orden: order + 1,
  };
};

/**
 * Funcion que me devuelve en forma de array todas las posiciones de la cancha
 */
export const getPlayerPositionListOnField = (
  playersOnField?: TacticOnField
) => {
  let positions: JSX.Element[] = [];

  for (const position in playersOnField) {
    const positionKey = position as unknown as keyof TacticOnField;
    const players = playersOnField?.[positionKey];

    if (players) {
      if (Array.isArray(players)) {
        positions = [...positions, ...players];
      } else {
        positions = [...positions, players];
      }
    }
  }

  return positions;
};

/**
 * Funcion que me devuelve los nombres de los puestos de cancha abreviados
 */
export const getPositionLabelByPositionId = (positionId: number) => {
  return PLAYER_POSITION_BY_ID[
    positionId as KeysOf<typeof PLAYER_POSITION_BY_ID>
  ];
};

/**
 * Esta funcion convierte el array de posiciones de JSX a un array de PlayerClub[]
 */
export const parseJSXElementToPlayerClub = (teamData: JSX.Element[]) => {
  return teamData.map((position) => {
    const positionData = position?.props as PlayerDataComponentInField;
    return positionData?.data;
  }) as PlayerClub[];
};

export const roundToNearestTen = (numero: number) => {
  const rest = numero % 10;
  if (rest < 5) {
    return Math.floor(numero / 10) * 10;
  } else {
    return Math.ceil(numero / 10) * 10;
  }
};

export const addAditionalZeroIfDecimal = (numberString: string) => {
  const floatNumber = parseFloat(numberString);

  if (isNaN(floatNumber) || !isFinite(floatNumber)) {
    return `-`;
  }

  const [intNumber, decimal] = numberString.split(',');

  if (decimal) {
    if (decimal.length > 1) {
      const parseDecimal = roundToNearestTen(parseInt(decimal));
      if (parseDecimal === 100) {
        return `${Number(intNumber) + 1}`;
      }

      if (parseDecimal === 0) {
        return `${intNumber}`;
      }

      return `${intNumber},${parseDecimal}`;
    } else {
      if (Number(decimal) === 0) {
        return `${intNumber}`;
      }

      return `${intNumber},${decimal}0`;
    }
  } else {
    return `${intNumber}`;
  }
};

export const addOpacityToHexColor = (hexColor: string, opacity: string) => {
  // Remove the # symbol from the hex color
  const hex = hexColor.replace('#', '');

  // Convert the hex color to RGB values
  const red = parseInt(hex.substring(0, 2), 16);
  const green = parseInt(hex.substring(2, 4), 16);
  const blue = parseInt(hex.substring(4, 6), 16);

  // Create the RGBA color string
  const rgbaColor = `rgb(${red} ${green} ${blue} / ${opacity} )`;

  return rgbaColor;
};

// Función para limpiar solo el state
export const clearLocationState = () => {
  window.history.replaceState(
    null, // nuevo state (null para limpiar)
    '',
    window.location.pathname + window.location.search
  );
};

/*
 * Evito mostrar dos veces el mismo toast
 */
let toast2101Shown = false;

export const showErrorToast = (error: GDTApiErrors | undefined) => {
  const errorToast = (msg: string | undefined) => {
    if (error?.codError === 2101) {
      if (toast2101Shown) return;
      toast2101Shown = true;
    }
    toast(msg, {
      containerId: 'global',
      role: 'alert',
      position:
        window.innerWidth > 1024
          ? toast.POSITION.TOP_CENTER
          : toast.POSITION.BOTTOM_LEFT,
      className: 'font-sans font-medium',
    });
  };

  if (error?.codError === 2101) {
    // AUTH_CONFUSION_USUARIOS
    errorToast(error?.mensaje);
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('reload-2101 (1)');
      return (window.location.href = ROUTES.basePath);
    }, 1000);
  } else if (
    error?.codError === 403 ||
    (error?.mensaje && error?.mensaje.includes('sesi'))
  ) {
    errorToast('Sesión expirada.');
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('logout-403 (1)');
      window.JSBridge?.notifyMessage('logout-403');
      return (window.location.href = ROUTES.basePath);
    }, 1000);
  } else {
    errorToast(error?.mensaje);
  }
};

export function navigateBasePath403(navigate: NavigateFunction) {
  // eslint-disable-next-line no-console
  console.log('logout-403 (2)');
  window.JSBridge?.notifyMessage('logout-403');
  navigate(ROUTES.basePath, { replace: true });
}

export function axiosErrorHandler<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  customErrorMessage?: string
) {
  return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try {
      return await fn(...args);
    } catch (error) {
      if (isAxiosError<GDTApiErrors>(error)) {
        if (
          error.response?.data?.codError === 2100 &&
          error.response.data.remCSRF
        ) {
          Cookies.set('XSRF-TOKEN', error.response.data.remCSRF);
        }
        showErrorToast(error.response?.data);
        if (error.response?.data?.mensaje) {
          throw new Error(error.response?.data.mensaje);
        } else {
          reportError('AxiosError - ' + error?.config?.url, error as Error);
          throw new Error(TEXTS.defaultError);
        }
      } else if (error instanceof Error) {
        showErrorToast({
          codError: -100,
          mensaje: error.message,
          estado: false,
        });
        reportError('Error' + error.message, error as Error);
        throw new Error(error.message);
      } else {
        showErrorToast({
          codError: -100,
          mensaje: TEXTS.defaultError,
          estado: false,
        });
        reportError('Error desconocido en axiosErrorHandler', error as Error);
        throw new Error(customErrorMessage ?? TEXTS.defaultError);
      }
    }
  };
}
/**
 * type predicate
 * @param response
 * @returns response.estado == false
 */
export function isApiError(
  response: any | GDTApiErrors
): response is GDTApiErrors {
  return (
    (response as GDTApiErrors).estado === false &&
    !!(response as GDTApiErrors).mensaje
  );
}

export function promiseTimeout(millis: number): Promise<GDTApiTimeout> {
  return new Promise(function (resolve) {
    setTimeout(resolve.bind(window, { timeout: true }), millis);
  });
}

/**
 * type predicate para ApiTimeout
 * @param response
 * @returns
 */
export function isApiTimeout(
  response: any | GDTApiTimeout
): response is GDTApiTimeout {
  return (response as GDTApiTimeout).timeout === true;
}

export const resolvePlayerPrice = (price: Optional<number>) => {
  if (price) {
    return price * 10_000;
  }

  return 0;
};

export const isFieldEmpty = (playersOnField?: TacticOnField) => {
  const positions = getPlayerPositionListOnField(playersOnField);
  return !positions.some(
    (position: PlayerComponentInField) => position.props?.data?.id
  );
};

export const getPlayersOnFieldCount = (playersField?: TacticOnField) => {
  if (playersField) {
    return (
      playersField.defenders.filter((player: PlayerComponentInField) => {
        return player.props?.data?.id;
      }).length +
      playersField.midfielders.filter((player: PlayerComponentInField) => {
        return player.props?.data?.id;
      }).length +
      playersField.forwards.filter((player: PlayerComponentInField) => {
        return player.props?.data?.id;
      }).length
    );
  }

  return 0;
};

export const tacticToString = (tactic: number, separator = ',') => {
  const maxValidId = TACTICS_ARR.length;

  const tacticId = Number.isInteger(tactic);

  if (!tacticId || tactic >= maxValidId || tactic < 0) {
    return null;
  }

  return TACTICS_ARR[tactic].join(separator);
};

export const capitalize = (str: string) => {
  if (typeof str !== 'string') {
    throw new Error('Input must be a string');
  }

  // Capitalize the first letter and concatenate the rest of the string
  return uppercaseFirstLetter(str) + str.slice(1);
};

export const modernCapitalize = (words: string) =>
  words
    .toLowerCase()
    .replace(/\b\w/g, (letter: string) => letter.toUpperCase());

export const isSelectedPlayerFromList = (
  result:
    | TemplatePlayer
    | PlayerClub
    | PlayerF5
    | PlayerMyTeamF5
    | PlayerHistoricF5
): result is TemplatePlayer => {
  return (result as TemplatePlayer).n !== undefined;
};

export const getShortNamePlayerPosition = (position: number) => {
  return PLAYER_POSITION_BY_ID[
    position as KeysOf<typeof PLAYER_POSITION_BY_ID>
  ];
};

export const countStringOccurrences = (str: string, collection: string[]) => {
  let count = 0;
  collection.forEach((item) => {
    if (item === str) {
      count++;
    }
  });
  return count;
};

export const nameAvatar = (fullNameUser: string) => {
  const nameChanging = fullNameUser.split(' ');
  const firstLetter = nameChanging[0].at(0);
  const secondLetter = nameChanging.at(-1)?.at(0);

  if (firstLetter && secondLetter) return firstLetter + secondLetter;
};

export const getValueFromObject = <T, K extends keyof T>(
  obj: T,
  key: K
): T[K] => {
  return obj[key];
};

export const uppercaseFirstLetter = (word: string) => {
  if (typeof word !== 'string') {
    throw new Error('Input must be a string');
  }
  return word.charAt(0).toUpperCase();
};
export const sortAlphabeticByLastName = (list: TemplatePlayer[]) => {
  return list.sort((a: TemplatePlayer, b: TemplatePlayer) => {
    return a.a.localeCompare(b.a);
  });
};

export const sortByGolThenLastName = (playersToOrder: TemplatePlayer[]) => {
  /* antes: groupByGols ..  */
  playersToOrder = playersToOrder || [];
  return playersToOrder.sort((a: TemplatePlayer, b: TemplatePlayer) => {
    const cmp = (b.gol ?? 0) - (a.gol ?? 0);
    if (cmp !== 0) {
      return cmp;
    }
    return a.a.localeCompare(b.a);
  });
};

export const alphanumericMaxLength = (
  value: string | number,
  maxLength: number
): boolean => {
  return (
    String(value).length <= maxLength &&
    String(value).match(ONLY_LETTERS_AND_NUMBERS) !== null
  );
};

type ObjectEntriesType = <T>(obj: T) => [keyof T, T[keyof T]][];
export const objectEntriesTyped: ObjectEntriesType =
  Object.entries as ObjectEntriesType;

export const oneSpaceBetweenWordMinTwoChar = (
  term: string,
  min?: number
): string => {
  if (!term) return '';
  // Remove leading spaces
  term = term.replace(/^\s+/, '');

  // Allow spaces between words only if there are more than 2 characters
  if (term.length > (min || 2)) {
    term = term.replace(/\s+/g, ' ');
  } else {
    term = term.replace(/\s+/g, '');
  }
  return term;
};

/*
 * La webview de android setea esta cookie.
 * Simular así:
 * document.cookie = 'isAndroid=true';
 */
export function isAndroid() {
  const isAndroid = Cookies.get('isAndroid') === 'true';
  return isAndroid;
}

/** versión no reactiva de useIsDesktop */
export const isDesktop = () => {
  const isDesktop = matchMedia(mediaSize).matches;
  return isDesktop;
};

export enum PlatformId {
  WEB = 1,
  APP_ANDROID = 4,
  MOBILE = 3,
}

/**
 * Backend: "EPlataforma"
 * Plataforma desde la que se accede a la aplicación
 * <li>WEB(1, "WEB")</li>
 * <li>MOBILE(3,"WEB MOBILE")</li>
 * <li>APP_ANDROID(4,"APP ANDROID")</li>
 * <li>APP_IOS(6,"APP IOS"),</li>
 */
export const getPlatformId: () => PlatformId = () => {
  if (isAndroid()) {
    return PlatformId.APP_ANDROID;
  }

  if (isDesktop()) {
    return PlatformId.WEB;
  }

  return PlatformId.MOBILE;
};

/*
export function agentAndroid() {
  return /Android/i.test(navigator.userAgent);
}

export function agentIOS() {
  // Comprueba la presencia de 'iPad', 'iPhone' o 'iPod'
  return (
    /iPad|iPhone|iPod/i.test(navigator.userAgent) && !('MSStream' in window)
  );
}
*/
export const isF5 = (context: EnumTeamContext) => {
  return [
    EnumTeamContext.Build5,
    EnumTeamContext.Team5,
    EnumTeamContext.Score5,
    EnumTeamContext.FilterTeam5,
  ].includes(context);
};

export const getParticipants = (amount: number, max: number, fem = false) => {
  if (amount === max) return fem ? 'Completa' : 'Completo';
  const rest = amount % (max - 1);
  const participants =
    (rest !== 0 ? 'Faltan  ' : 'Falta ') +
    (max - amount || 0) +
    ' ' +
    (rest ? GENERICS_WORDS.participants : GENERICS_WORDS.participant);

  return participants;
};

export const participantsMl = (amount: number, maxNumParticipants: number) => {
  if (amount === maxNumParticipants) return 'Completa';
  if (amount === 0) return '';
  const rest = amount % (maxNumParticipants - 1);
  const participants =
    (rest !== 0 ? 'Faltan  ' : 'Falta ') +
    (maxNumParticipants - amount || 0) +
    ' ' +
    (rest ? GENERICS_WORDS.participants : GENERICS_WORDS.participant);

  return participants;
};

export const encodeParam = (param: string | null) => {
  // var escapado = escape(param);//(ISO)
  // return escapado.replace(/\+/g,"%2B").replace(/\%u200B/g,"");//evito que se reinterprete el "+"
  return encodeURIComponent(param || '');
};

const reportToServer = false;
// let lastError = null;
/**
 * reporta error JS a:
 * 1) consola
 * 2) si esta habilitado el reporte de crashes: server
 */
export function reportError(sMsg: string, e: Error | undefined) {
  const err =
    sMsg + ' - e.msg: ' + (e || {}).message + ',stack:' + (e || {}).stack;
  // eslint-disable-next-line no-console
  console.log('ERROR: ' + err);
  const excepciones = !reportToServer;
  if (!excepciones) {
    reportErrorServer(err);
  }
  // dataLayer.push({"event": "gdtJsError","eMessage": e.message, "eStack": e.stack});
}

/**
 * Quiero ver este crash en el server, incondicionalmente
 * @param err Mensaje para el log productivo
 */
export function reportErrorServer(err: string) {
  fetch(URLs.REPORT_CRASH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ log: err }),
  });
}

export function extractNumberWithHash(text: string | undefined): string | null {
  if (!text) return null;
  const match = text.match(/#\d+$/);
  return match ? match[0] : null;
}

export function capitalizeFirstLetter(str?: string) {
  if (!str) return '';
  return uppercaseFirstLetter(str) + str.slice(1).toLowerCase();
}

export function toLowerCase(str?: string) {
  if (!str) return '';
  return str.slice(0).toLowerCase();
}

export const onceSessionUtil = {
  /**
   * return para cada user + key (en page session, sessionStorage),
   * 		retorna true cada X minutos.
   */
  everyXMinutes: (minutes: number, key: string, passId: string) => {
    const MINUTO = 60 * 1000;
    const idPase = passId || '0';
    const sKey = idPase + '_' + key;
    const timeChecked = parseInt(sessionStorage.getItem(sKey) || '0', 10);
    const now = new Date().getTime();
    if (now > timeChecked + minutes * MINUTO) {
      sessionStorage.setItem(sKey, String(now));
      return true;
    }
    return false;
  },

  setPreviouslyChecked: (key: string, passId: string) => {
    const tmp = !onceSessionUtil.everyXMinutes(5, key, passId);
    // eslint-disable-next-line no-console
    console.debug('isPreviouslyChecked..' + key + '=' + tmp);
    return tmp;
  },

  setPreviouslyShown: (key: string, passId: string) => {
    const tmp = !onceSessionUtil.everyXMinutes(10000, key, passId);
    // eslint-disable-next-line no-console
    console.debug('isPreviouslyShown..' + key + '=' + tmp);
    return tmp;
  },
};

export const formatearNumero = (num: number | undefined) => {
  return num ? new Intl.NumberFormat('de-DE').format(num) : '-';
};

export const letterGenderById = (id?: string): string => {
  if (!id) return '';
  return parseInt(id) === 1 ? 'M' : 'F';
};

declare global {
  interface Window {
    AndroidShareHandler?: {
      share: (title: string, text: string, url: string) => void;
    };
  }
}

// TODO: Ver hacer integracion con android nativo
export const triggerNativeShare = ({
  title,
  text,
  url,
}: {
  title: string;
  text: string;
  url: string;
}) => {
  // Check if the native handler is available (for Android WebView)
  if (
    window.AndroidShareHandler &&
    typeof window.AndroidShareHandler.share === 'function'
  ) {
    window.AndroidShareHandler.share(title, text, url);
  } else if (navigator.share) {
    // Fallback for standard mobile browsers (if the WebView implementation isn't used)
    return (
      navigator
        .share({
          title,
          text,
          url,
        })
        // eslint-disable-next-line no-console
        .then(() => console.log('Shared successfully'))
        .catch((error) => console.error('Share failed:', error))
    );
  } else {
    // Fallback for environments where neither is supported (e.g., desktop web)
    // eslint-disable-next-line no-console
    console.log('Web Share not supported, use alternative method.');
  }
};

// shareUrl.ts
export function buildShareUrl({
  type,
  id,
  title,
  desc,
  img,
}: {
  type: 'copa' | 'miniliga' | 'torneo';
  id: string;
  title: string;
  desc: string;
  img: string;
}) {
  const base = window.location.origin;

  const params = new URLSearchParams({
    id,
    title,
    desc,
    img,
  });

  return `${base}/share/${type}?${params.toString()}`;
}

// Manejador de invitacion por link
export const handleInvitationUrl = (
  fullUrlOrPath: string
): false | InvitationType => {
  let pathname: string;

  try {
    // Soporta URL completa o solo pathname
    const url = new URL(fullUrlOrPath, window.location.origin);
    pathname = url.pathname;
  } catch {
    return false;
  }

  const patterns: { pattern: RegExp; type: InvitationType }[] = [
    {
      pattern: /^\/torneo-de-amigos\/invitar\/(\d+)$/,
      type: InvitationType.TORNEO,
    },
    {
      pattern: /^\/copa-de-amigos\/invitar\/(\d+)$/,
      type: InvitationType.COPA,
    },
    {
      pattern: /^\/miniligas\/invitar\/(\d+)$/,
      type: InvitationType.MINILIGA,
    },
  ];

  for (const { pattern, type } of patterns) {
    const match = pathname.match(pattern);

    if (match) {
      const id = match[1];

      // Seguridad extra (aunque el regex ya lo garantiza)
      if (!/^\d+$/.test(id)) return false;

      localStorage.setItem('invitationUrl', pathname);
      localStorage.setItem('invitationId', id);
      localStorage.setItem('invitationType', type);

      return type;
    }
  }

  return false;
};

export const handleFaqUrl = (pathname: string): boolean | string => {
  // Obtener el hash de la URL completa (no solo pathname)
  const fullUrl = window.location.href;
  const hashIndex = fullUrl.indexOf('#');
  const hash = hashIndex !== -1 ? fullUrl.substring(hashIndex + 1) : '';

  // Patrones para capturar las diferentes secciones de preguntas frecuentes
  // Simplificar los patrones para hacerlos más robustos
  const registracionPattern = /\/preguntas-frecuentes\/Registracion/;
  const juegoPattern = /\/preguntas-frecuentes\/Juego/;
  const puntajesPattern = /\/preguntas-frecuentes\/Puntajes/;
  const torneosPattern = /\/preguntas-frecuentes\/Torneos/;
  const pinesPattern = /\/preguntas-frecuentes\/Pines/;

  // Verificar cada patrón
  let section = '';
  if (registracionPattern.test(pathname)) {
    section = 'RegistrationAndLogin';
  } else if (juegoPattern.test(pathname)) {
    section = 'TeamBuildingAndAwards';
  } else if (puntajesPattern.test(pathname)) {
    section = 'Scores';
  } else if (torneosPattern.test(pathname)) {
    section = 'SpecialTournaments';
  } else if (pinesPattern.test(pathname)) {
    section = 'PinsAndPremiumPack';
  }

  // Si hay coincidencia con alguno de los patrones
  if (section) {
    // Guardar la información en localStorage para que el componente Faqs pueda acceder a ella
    localStorage.setItem('faqSection', section);
    if (hash) {
      localStorage.setItem('faqAccordionId', hash);
    } else {
      localStorage.removeItem('faqAccordionId');
    }
    return true;
  }
  return false;
};

export const cleanStorageRedirectInvitation = () => {
  localStorage.removeItem('invitationId');
  localStorage.removeItem('invitationUrl');
  localStorage.removeItem('invitationType');
};
